/**
 * Caption-language UX helpers shared by surfaces that drive the karaoke
 * producer's `KaraokeConfig.language` from a user-facing language picker
 * (Creator Studio's CaptionsLanguageHeaderToggle, Content Studio's
 * ClipTranscriptBridge, etc.).
 *
 * `KaraokeConfig.language` is a free-form string with two values that mean
 * "render the source language": `'original'` (canonical) and `'english'`
 * (legacy, retained until Epic 2 T48/T49 retires the SubtitlesProcessor
 * path). Picking either of these from the toggle should be treated the
 * same — and a `null` selection from the toggle (= "back to original")
 * should also normalize to the same bucket.
 */

import { normalizeLangKey } from './bcp47';

export const ORIGINAL_LANGUAGE = 'original';

export const isOriginalLang = (lang: string | null | undefined): boolean =>
  !lang || lang === ORIGINAL_LANGUAGE || lang === 'english';

/**
 * PLAT-10788 — the canonical BCP-47 key for the source/original caption variant:
 * the transcript's own `language` when present, else the story's selected
 * `originalVideoLanguage`, else `'en'`. `rawTranscriptLanguage` must be the
 * transcript's RAW `language` (read before `TranscriptionStore`'s internal
 * `|| 'en'` coalesce, so the `originalVideoLanguage` fallback stays reachable).
 */
export const resolveSourceLanguageKey = (
  rawTranscriptLanguage: string | null | undefined,
  originalVideoLanguage: string | null | undefined,
): string =>
  normalizeLangKey(rawTranscriptLanguage || originalVideoLanguage || 'en');

/**
 * PLAT-10788 — the canonical per-language karaoke key for a caption variant,
 * given its `KaraokeConfig.language`. The `'original'`/`'english'`/empty
 * sentinels (`isOriginalLang`) resolve to the source key; a real BCP-47 code is
 * canonicalized. Single chokepoint so save/load/switch key identically.
 */
export const resolveCaptionLanguageKey = (
  configLanguage: string | null | undefined,
  rawTranscriptLanguage: string | null | undefined,
  originalVideoLanguage: string | null | undefined,
): string => {
  if (!configLanguage || isOriginalLang(configLanguage)) {
    return resolveSourceLanguageKey(
      rawTranscriptLanguage,
      originalVideoLanguage,
    );
  }
  return normalizeLangKey(configLanguage);
};

/**
 * Whether a caption/transcript language code is Japanese, keyed on the base
 * language so every regional/script variant matches (`ja`, `ja-jp`,
 * `ja-Jpan`, …). Used to gate the Japanese caption fonts in the pickers
 * (PLAT-10420). For new code prefer `resolveCaptionScript` (PLAT-11408) —
 * it is script-accurate where this helper is base-language-only, so the two
 * deliberately diverge on script-overridden tags like `ja-Latn` (this returns
 * true; the resolver returns `'latin'`).
 */
export const isJapaneseLang = (lang: string | null | undefined): boolean =>
  !!lang && lang.toLowerCase().split('-')[0] === 'ja';

/**
 * The writing systems caption fonts are curated for (PLAT-11408, epic
 * PLAT-11407). Chinese splits by variant because Simplified and Traditional
 * need different font files (`Noto Sans SC` vs `Noto Sans TC`).
 */
export type CaptionScript =
  | 'latin'
  | 'cyrillic'
  | 'japanese'
  | 'chinese-simplified'
  | 'chinese-traditional'
  | 'korean';

/**
 * ISO 15924 script codes (as carried by a BCP-47 script subtag or the Dato
 * `language.script` field) mapped to our `CaptionScript` buckets — plus the
 * bucket names themselves as identity entries so a hint may also be one of
 * our own literals. Always read through `hasOwn`: a key colliding with an
 * `Object.prototype` member (`'constructor'`, `'__proto__'`) must miss, not
 * return a function.
 */
const SCRIPT_CODE_TO_CAPTION_SCRIPT: Record<string, CaptionScript> = {
  latn: 'latin',
  cyrl: 'cyrillic',
  jpan: 'japanese',
  hira: 'japanese',
  kana: 'japanese',
  hans: 'chinese-simplified',
  hant: 'chinese-traditional',
  kore: 'korean',
  hang: 'korean',
  hrkt: 'japanese',
  jamo: 'korean',
  bopo: 'chinese-traditional',
  hanb: 'chinese-traditional',
  // `hani` (generic Han) is intentionally unmapped: the base language must
  // decide (`zh-Hani` → simplified default, `ja-Hani` → japanese).
  latin: 'latin',
  cyrillic: 'cyrillic',
  japanese: 'japanese',
  'chinese-simplified': 'chinese-simplified',
  'chinese-traditional': 'chinese-traditional',
  korean: 'korean',
};

const hasOwn = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

/**
 * Base languages whose likely script is Cyrillic (conservative CLDR
 * likely-script defaults; extend as new caption languages onboard).
 */
const CYRILLIC_BASE_LANGS = new Set([
  'ru',
  'uk',
  'be',
  'bg',
  'mk',
  'sr',
  'kk',
  'ky',
  'mn',
  'tg',
]);

/** Regions where `zh` defaults to Traditional Chinese. */
const TRADITIONAL_CHINESE_REGIONS = new Set(['tw', 'hk', 'mo']);

const ALPHA4_SUBTAG = /^[a-z]{4}$/;
const ALPHA2_SUBTAG = /^[a-z]{2}$/;
const PLAUSIBLE_BCP47_TAG = /^[a-z]{2,8}(-[a-z0-9]{1,8})*$/i;

const resolveScriptFromHint = (
  hint: string | null | undefined,
): CaptionScript | null => {
  if (!hint) return null;
  const key = hint.trim().toLowerCase();
  if (key && hasOwn(SCRIPT_CODE_TO_CAPTION_SCRIPT, key)) {
    return SCRIPT_CODE_TO_CAPTION_SCRIPT[key];
  }
  return null;
};

/**
 * PLAT-11408 — resolve a caption/transcript language code to the writing
 * system its captions render in. Total and never throws; anything
 * unrecognized (including `''`/`null`, malformed tags, and the
 * `'original'`/`'english'` sentinels — resolve those through
 * `resolveCaptionLanguageKey` first) falls back to `'latin'`, which
 * downstream means "no font substitution".
 *
 * Resolution order:
 *  1. A recognized `scriptHint` (e.g. the Dato `language.script` field) wins;
 *     an unrecognized hint falls through rather than defaulting.
 *  2. A recognized script subtag inside the tag itself (`zh-Hans-TW`,
 *     `sr-Latn`, `ja-Hrkt`). An unrecognized one (generic `Hani`, a code we
 *     don't model like `mn-Mong`) defers to step 3 — for CJK bases that
 *     recovers the right bucket, and for genuinely foreign scripts the base
 *     guess is no worse than refusing.
 *  3. A static base-language (+ region/extlang for `zh`) map.
 */
export const resolveCaptionScript = (
  lang: string | null | undefined,
  scriptHint?: string | null,
): CaptionScript => {
  const hinted = resolveScriptFromHint(scriptHint);
  if (hinted) return hinted;
  if (!lang) return 'latin';
  // POSIX/ASR pipelines emit underscore forms (`ja_JP`); normalize locally —
  // NOT inside `normalizeLangKey`, whose exact output keys persisted
  // per-language caption variants.
  const dashed = lang.trim().replace(/_/g, '-');
  if (!dashed) return 'latin';
  // Only run plausible BCP-47 shapes through Intl canonicalization:
  // `normalizeLangKey` logs a warning on garbage, and this resolver is
  // documented to accept arbitrary strings quietly.
  const canonical = PLAUSIBLE_BCP47_TAG.test(dashed)
    ? normalizeLangKey(dashed)
    : dashed;
  const parts = canonical.toLowerCase().split('-');
  const base = parts[0];
  // Subtags at and after a singleton (`-u-`, `-x-`, …) are extension /
  // private-use values, not script or region — stop the scan there.
  let mainEnd = parts.length;
  for (let i = 1; i < parts.length; i += 1) {
    if (parts[i].length === 1) {
      mainEnd = i;
      break;
    }
  }
  const main = parts.slice(1, mainEnd);
  const scriptSubtag = main.find(subtag => ALPHA4_SUBTAG.test(subtag));
  if (scriptSubtag && hasOwn(SCRIPT_CODE_TO_CAPTION_SCRIPT, scriptSubtag)) {
    return SCRIPT_CODE_TO_CAPTION_SCRIPT[scriptSubtag];
  }
  if (base === 'ja') return 'japanese';
  if (base === 'ko') return 'korean';
  // Cantonese: ASR/translation providers emit `yue` (not `zh-*`), and
  // written Cantonese uses Traditional characters.
  if (base === 'yue') return 'chinese-traditional';
  if (base === 'zh') {
    // Legacy extlang form of Cantonese (`zh-yue`), which Intl refuses to
    // canonicalize.
    if (main[0] === 'yue') return 'chinese-traditional';
    const region = main.find(subtag => ALPHA2_SUBTAG.test(subtag));
    return region && TRADITIONAL_CHINESE_REGIONS.has(region)
      ? 'chinese-traditional'
      : 'chinese-simplified';
  }
  if (CYRILLIC_BASE_LANGS.has(base)) return 'cyrillic';
  return 'latin';
};

/**
 * PLAT-9621 — cross-surface notification that the caption language changed, so
 * a surface that owns URL or in-memory selection state can follow. Lives here
 * rather than on the component that first dispatched it: the store layer
 * broadcasts it too (undo/redo restoring a language, PLAT-10816), and stores
 * must not import from components.
 */
export const CAPTIONS_LANGUAGE_CHANGED_EVENT =
  'arbor-captions-language-changed';

export type CaptionsLanguageChangedDetail = { language: string | null };
